/**
 * Авторизация PWA-версии (вне Telegram).
 *
 * В Telegram человек уже опознан подписью `initData` — тут ничего не нужно.
 * В обычном браузере вход идёт через Google/почту: сервер после входа
 * возвращает нас на `/#session=<токен>`, мы прячем токен в localStorage и
 * шлём его в API как `Authorization: Bearer`. Дальше приложение одинаково для
 * обоих способов — разница только в том, чем подписан запрос.
 */
import { inTelegram } from './telegram'
import type { Lang } from '../data/cards'

const SESSION_KEY = 'sirr_session'

/** 'telegram' — внутри клиента Telegram; 'pwa' — обычный браузер. */
export const authMode: 'telegram' | 'pwa' = inTelegram ? 'telegram' : 'pwa'

let capturedError: string | null = null

/**
 * Сколько звёзд начислено за только что состоявшийся вход.
 *
 * Приходит вместе с токеном — во фрагменте после Google или в теле ответа при
 * входе по почте, — а не спрашивается отдельной ручкой. Причина простая: по
 * профилю «начислено только что» от «начислено месяц назад» уже не отличить,
 * там лежит один флаг. Читается один раз и забывается: окно «начислено»
 * показывается за вход, а не за каждое открытие главной.
 */
let capturedLogin = false
let capturedBonus = 0

/**
 * Факт входа хранится отдельно от суммы: награда за вход обнулена (за него
 * теперь дают первое гадание), и «вошёл только что» больше не следует из
 * «начислено больше нуля». Иначе вместе с наградой пропало бы и окно, которым
 * мы подтверждаем подарок.
 */
export function takeLogin(): { justLoggedIn: boolean; stars: number } {
  const out = { justLoggedIn: capturedLogin, stars: capturedBonus }
  capturedLogin = false
  capturedBonus = 0
  return out
}

/**
 * Забирает результат входа из фрагмента адреса сразу при загрузке.
 * Фрагмент (`#…`) выбран не случайно: он не уходит ни в логи сервера, ни в
 * заголовок Referer. После разбора стираем его из адреса, чтобы токен не висел
 * в строке и не попал в историю/закладки.
 */
function consumeHash(): void {
  const hash = window.location.hash.slice(1)
  if (!hash) return
  const params = new URLSearchParams(hash)
  const token = params.get('session')
  const error = params.get('auth_error')
  if (token) localStorage.setItem(SESSION_KEY, token)
  if (token) {
    capturedLogin = true
    capturedBonus = Number(params.get('bonus') ?? 0) || 0
  }
  if (error) capturedError = error
  if (token || error) {
    // Убираем фрагмент, сохранив путь и query.
    history.replaceState(null, '', window.location.pathname + window.location.search)
  }
}

if (authMode === 'pwa') consumeHash()

/** Токен сессии PWA или null. В Telegram всегда null (там своя подпись). */
export function sessionToken(): string | null {
  return authMode === 'pwa' ? localStorage.getItem(SESSION_KEY) : null
}

export function setSession(token: string): void {
  localStorage.setItem(SESSION_KEY, token)
}

export function clearSession(): void {
  localStorage.removeItem(SESSION_KEY)
}

/**
 * Считается ли человек вошедшим. В Telegram — всегда (подпись initData на
 * каждом запросе). В браузере — только при наличии токена сессии.
 *
 * Внимание: сессия бывает и АНОНИМНОЙ, то есть «вошедший» тут значит лишь
 * «сервер знает, кто это», а не «человек назвался». Назвался он или нет,
 * говорит поле `anon` в профиле — знает это только сервер.
 */
export function isAuthed(): boolean {
  return authMode === 'telegram' || Boolean(sessionToken())
}

/**
 * Заводит анонимную сессию, если своей ещё нет.
 *
 * Это и есть снятие стены входа: приложение открывается сразу, а вход
 * становится предложением («сохранить историю», «купить звёзды», «второе
 * устройство»), а не воротами. Раньше человек отдавал аккаунт Google раньше,
 * чем видел первую карту, — за 15 дней 1333 адреса открыли сайт, а вход начали
 * четверо.
 *
 * В Telegram не нужна вовсе: там человек опознан подписью.
 */
export async function ensureSession(lang: Lang): Promise<void> {
  if (authMode === 'telegram' || sessionToken()) return
  const res = await fetch('/auth/anon', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ lang }),
  })
  if (!res.ok) throw new AuthError(`HTTP ${res.status}`)
  const data = (await res.json()) as { token?: string }
  if (!data.token) throw new AuthError('Пустой ответ сервера')
  setSession(data.token)
}

/**
 * Просит сервер запомнить: «войду — присвой мне мой же аккаунт».
 *
 * Нужно только Google-входу: он уходит переадресацией, и заголовка с токеном
 * на возврате уже не будет. Ответ — одноразовый код, который прицепляется к
 * адресу старта. Сам id аккаунта в адрес не кладём: его видно и подделать
 * значит присвоить чужую историю, поэтому сервер выясняет владельца сам.
 *
 * Не аноним (или Telegram) — присваивать нечего, возвращаем null молча.
 */
async function linkCode(): Promise<string | null> {
  const token = sessionToken()
  if (!token) return null
  try {
    const res = await fetch('/auth/link/prepare', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!res.ok) return null
    const data = (await res.json()) as { code?: string }
    return data.code ?? null
  } catch {
    return null
  }
}

/** Код ошибки входа из последнего возврата (`google`/`state`) или null. */
export function authError(): string | null {
  return capturedError
}

/**
 * Уводит на вход через Google, прихватив просьбу присвоить анонимный аккаунт.
 *
 * Раньше это был просто адрес, но теперь перед уходом надо успеть спросить у
 * сервера одноразовый код, — иначе человек, гадавший не назвавшись, войдёт в
 * пустой аккаунт, а вся его история останется на прежнем.
 */
export async function goToGoogleLogin(lang: Lang, back?: string): Promise<void> {
  // Куда вернуть человека после входа. Вход через Google уводит со страницы
  // целиком, и после возврата приложение загружается заново — с главной. Но
  // шёл-то человек не на главную, а покупать: не вернув его туда, откуда он
  // ушёл, мы заставляем искать ту же кнопку второй раз.
  if (back) localStorage.setItem(RETURN_KEY, back)
  const code = await linkCode()
  const link = code ? `&link=${encodeURIComponent(code)}` : ''
  window.location.href = `/auth/google/start?lang=${lang}${link}`
}

const RETURN_KEY = 'sirr_after_login'

/** Куда вернуться после входа; читается один раз и сразу забывается. */
export function takeReturnScreen(): string | null {
  const v = localStorage.getItem(RETURN_KEY)
  if (v) localStorage.removeItem(RETURN_KEY)
  return v
}

/**
 * Сообщает приложению, что сессия истекла (сервер ответил 401). Чистит токен
 * и просит перерисоваться на экран входа — без перезагрузки страницы.
 */
export function expireSession(): void {
  clearSession()
  window.dispatchEvent(new Event('sirr-auth-expired'))
}

/** Ошибка входа по почте с кодом от сервера (`bad_credentials` и т.п.). */
export class AuthError extends Error {
  constructor(
    message: string,
    readonly code?: string,
  ) {
    super(message)
  }
}

/**
 * Завершает вход: сохраняет токен и сообщает приложению перерисоваться в
 * приложение (симметрично `expireSession`). Без перезагрузки страницы.
 */
function completeLogin(token: string): void {
  setSession(token)
  window.dispatchEvent(new Event('sirr-authed'))
}

async function postAuth(path: string, body: Record<string, unknown>): Promise<void> {
  // Токен текущей сессии прикладываем намеренно: если это анонимная сессия,
  // сервер присвоит человеку его же аккаунт вместе с историей, а не заведёт
  // пустой. Кого присваивать, решает сервер по токену — мы лишь предъявляем
  // его, как на любом другом запросе.
  const token = sessionToken()
  const res = await fetch(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    let message = `HTTP ${res.status}`
    let code: string | undefined
    try {
      const b = (await res.json()) as { error?: string; code?: string }
      message = b.error ?? message
      code = b.code
    } catch {
      /* тело не JSON */
    }
    throw new AuthError(message, code)
  }
  const data = (await res.json()) as { token?: string; bonus?: number }
  if (!data.token) throw new AuthError('Пустой ответ сервера')
  capturedLogin = true
  capturedBonus = data.bonus ?? 0
  completeLogin(data.token)
}

/** Регистрация по почте: создаёт аккаунт и сразу входит. */
export function emailRegister(email: string, password: string, firstName: string, lang: Lang): Promise<void> {
  return postAuth('/auth/email/register', { email, password, firstName, lang })
}

/** Вход по почте и паролю. */
export function emailLogin(email: string, password: string): Promise<void> {
  return postAuth('/auth/email/login', { email, password })
}

/**
 * Выход по кнопке. Гасим сессию и на сервере, и локально. Ошибку сети
 * глотаем: локально мы вышли в любом случае, а на сервере она протухнет сама.
 */
export async function logout(): Promise<void> {
  const token = sessionToken()
  if (token) {
    try {
      await fetch('/auth/logout', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      })
    } catch {
      /* нет сети — не беда, выходим локально */
    }
  }
  expireSession()
}
